'use client';
import { Img } from '@/components/blocks/MaterialImage';
import { EMarketingSpot } from '@/components/content/EMarketingSpot';
import {
	PDP_ADDITIONAL_INFO_INFERIOR_SECTION_ESPOT,
	PDP_ADDITIONAL_INFO_MIDDLE_SECTION_ESPOT,
} from '@/components/content/HdmAditionalInfo/constans';
import { ReportProblemLink } from '@/components/content/HdmAditionalInfo/parts/ReportAProblemLink';
import { styles } from '@/components/content/HdmAditionalInfo/styles/style';
import {
	NAVIGABLE_SECTION_IDS,
	useNavigableAccordionSection,
} from '@/components/content/ProductDetails/hooks/useNavigableAccordionSection';
import { useBreadcrumbTrail } from '@/data/Content/BreadcrumbTrail';
import { useDefaultStore } from '@/data/Content/DefaultStore';
import { verifyAttachments } from '@/data/Content/Product-Server';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useLocalization } from '@/data/Localization';
import { WidgetComponentProps } from '@/data/types/Slot';
import { isKitOrBundleType } from '@/utils/productIsA';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Grid,
	Typography,
	useMediaQuery,
	useTheme,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { CDN_BASE_URL, downloadFiles } from 'shared/custom/constants/commonConstants';
import { DownloadItems } from './parts/DownloadItems';

export const HdmAditionalInfo: FC<WidgetComponentProps> = ({ id }) => {
	const navigableSectionProps = useNavigableAccordionSection({
		sectionId: NAVIGABLE_SECTION_IDS.PRODUCT_ADDITIONAL_INFO,
	});
	const isExpanded = navigableSectionProps.expanded;

	const { uniqueId: physicalStoreId } = useDefaultStore();
	const {
		product: catentry,
		selection,
		attachments,
		isBuyable,
	} = useProductDetails({
		partNumber: id.toString(),
		physicalStoreIds: [physicalStoreId ?? ''],
	});
	const isKitOrBundle = isKitOrBundleType[catentry?.type ?? 'unknown'];
	const { departments } = useBreadcrumbTrail();
	const product = !isKitOrBundle ? selection?.sku : catentry;
	const [downloadableFiles, setDownloadableFiles] = useState<any[]>([]);
	const [filesLoading, setFilesLoading] = useState(true);
	const { AdditionalInfo } = useLocalization('HDMProductDetail');
	const theme = useTheme();
	const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('lg'));
	const [imageError, setImageError] = useState(false);

	useEffect(() => {
		const checkFiles = async () => {
			if (!product || !attachments.length) {
				setDownloadableFiles([]);
				setFilesLoading(false);
				return;
			}

			const validatedFiles = [];

			for (const fileDef of downloadFiles) {
				const matchingAttachment = attachments.find(
					(attachment) =>
						attachment.usage === 'DOCUMENTS' &&
						attachment.attachmentAssetPath.endsWith(
							`productos/${product.partNumber}/${product.partNumber}${fileDef.fileName}`
						)
				);

				if (!matchingAttachment) continue;

				const verified = await verifyAttachments([matchingAttachment]);

				if (verified.length === 0) continue;

				const url = `${CDN_BASE_URL}/${matchingAttachment.identifier}`;

				validatedFiles.push({
					...fileDef,
					url,
				});
			}

			setDownloadableFiles(validatedFiles);
			setFilesLoading(false);
		};

		if (attachments.length > 0 && filesLoading && downloadableFiles.length === 0) {
			checkFiles();
		}
	}, [attachments, product, filesLoading, downloadableFiles.length]);

	const productImage = attachments.find((attachment) =>
		attachment.attachmentAssetPath.endsWith(`${product?.partNumber}-xd.jpg`)
	);

	if (!product || !isBuyable) {
		return null;
	}
	const shouldShowImage = productImage && !imageError;

	return (
		<Accordion
			id="additionalInfoCard"
			sx={{
				width: '100%',
			}}
			disableGutters
			{...navigableSectionProps}
		>
			<AccordionSummary
				sx={styles.accordionSummary}
				expandIcon={isExpanded ? <RemoveIcon /> : <AddIcon />}
			>
				<Typography variant="headingSegment" component="h3">
					{AdditionalInfo.t()}
				</Typography>
			</AccordionSummary>
			<AccordionDetails>
				<Grid container spacing={3}>
					<Grid
						item
						xs={12}
						lg={shouldShowImage ? 6 : 12}
						sx={{
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'space-between',
							minHeight: '100%',
							pr: { lg: shouldShowImage ? 2.5 : 0 },
						}}
					>
						<Box>
							{!isMobileOrTablet ? (
								<EMarketingSpot
									id={PDP_ADDITIONAL_INFO_MIDDLE_SECTION_ESPOT}
									properties={{
										emsName: PDP_ADDITIONAL_INFO_MIDDLE_SECTION_ESPOT,
									}}
								/>
							) : null}
							{filesLoading || !product
								? null
								: downloadableFiles && (
										<Box
											sx={{
												...styles.downloadablesSection,
												flexDirection: {
													xs: 'column',
													lg: shouldShowImage ? 'column' : 'row',
												},
												justifyContent: {
													xs: 'initial', // Default for small screens
													md: shouldShowImage ? 'initial' : 'space-between',
												},
											}}
										>
											<DownloadItems downloadableFiles={downloadableFiles} />
										</Box>
								  )}
							{isMobileOrTablet ? (
								<EMarketingSpot
									id={PDP_ADDITIONAL_INFO_MIDDLE_SECTION_ESPOT}
									properties={{
										emsName: PDP_ADDITIONAL_INFO_MIDDLE_SECTION_ESPOT,
									}}
								/>
							) : (
								<EMarketingSpot
									id={PDP_ADDITIONAL_INFO_INFERIOR_SECTION_ESPOT}
									properties={{
										emsName: PDP_ADDITIONAL_INFO_INFERIOR_SECTION_ESPOT,
									}}
								/>
							)}
							{product?.longDescription ? (
								<Typography
									variant="body1"
									component="div"
									sx={{ mb: '20px' }}
									dangerouslySetInnerHTML={{ __html: product.longDescription }}
								/>
							) : null}
							{isMobileOrTablet ? (
								<EMarketingSpot
									id={PDP_ADDITIONAL_INFO_INFERIOR_SECTION_ESPOT}
									properties={{
										emsName: PDP_ADDITIONAL_INFO_INFERIOR_SECTION_ESPOT,
									}}
								/>
							) : null}
							{product?.legalDescription ? (
								<Typography variant="body1" sx={{ mb: '20px' }}>
									{product.legalDescription}
								</Typography>
							) : null}
						</Box>
					</Grid>
					{shouldShowImage ? (
						<Grid item xs={12} lg={6} sx={{ maxWidth: { lg: '800px' }, pl: { lg: 2.5 } }}>
							<Box sx={styles.imageSection}>
								<Img
									src={CDN_BASE_URL + '/' + productImage.identifier}
									alt={product?.name || 'Image'}
									onError={() => setImageError(true)}
								/>
							</Box>
						</Grid>
					) : null}
				</Grid>
				<Box sx={{ textAlign: { xs: 'center', lg: 'left' } }}>
					<ReportProblemLink sku={product.partNumber} name={product?.name} departmentPDP={departments}/>
				</Box>
			</AccordionDetails>
		</Accordion>
	);
};
